import apiClient from './apiClient';
import { cachedGet } from './sessionConfigCache';

// ==================== Notifications API ====================

export const notificationsAPI = {
  // Get all notifications
  // `skipAuthRedirect` so the 12s polling loop in DashboardLayout never
  // force-logs the user out on a transient 401. (See apiClient.js)
  getAll: async (limit = 50, unreadOnly = false, type = null) => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    if (unreadOnly) params.append('unread_only', 'true');
    if (type) params.append('notification_type', type);
    const response = await apiClient.get(`/notifications?${params.toString()}`, { skipAuthRedirect: true });
    return response.data;
  },

  // Get unread count
  getUnreadCount: async () => {
    const response = await apiClient.get('/notifications/unread-count', { skipAuthRedirect: true });
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    const response = await apiClient.post(`/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark all as read
  markAllAsRead: async () => {
    const response = await apiClient.post('/notifications/mark-all-read');
    return response.data;
  },

  // Consolidated badge counts (new-lead / chat / verifications / missing_pay / leads).
  // Backed by GET /api/badges — the single, tenant-scoped, ack-adjusted source
  // the NotificationProvider polls every 5s. Uses skipAuthRedirect so the
  // poller never bounces the user on a transient 401.
  getBadges: async () => {
    const response = await apiClient.get('/badges', { skipAuthRedirect: true });
    return response.data;
  },

  // Record "I've checked this category" — bumps lastSeenAt for the caller so
  // the derived count in /api/badges drops on the next poll. Categories:
  // bell, chat, verifications, missing_pay, payouts, team, leads.
  ackCategory: async (category) => {
    const response = await apiClient.post('/notifications/ack', { category });
    return response.data;
  },

  // One-time-per-user baseline: stamp lastSeenAt=now for any category the
  // user has NEVER acked. The pre-existing backlog stops badging, but any
  // new item (createdAt > lastSeenAt) still shows. Idempotent — safe to
  // call every mount; once every category has a row it's a no-op.
  ackBaseline: async () => {
    const response = await apiClient.post('/notifications/ack-baseline', {});
    return response.data;
  },

  // Delete notification
  delete: async (notificationId) => {
    const response = await apiClient.delete(`/notifications/${notificationId}`);
    return response.data;
  },

  // Clear all notifications
  clearAll: async () => {
    const response = await apiClient.delete('/notifications/clear-all');
    return response.data;
  },

  // Generate event reminders
  generateEventReminders: async () => {
    const response = await apiClient.post('/notifications/generate/event-reminder');
    return response.data;
  },

  // Generate understaffed alerts
  generateUnderstaffedAlerts: async () => {
    const response = await apiClient.post('/notifications/generate/understaffed-alert');
    return response.data;
  },

  // Generate missing pay alerts for completed events
  generateMissingPayAlerts: async () => {
    const response = await apiClient.post('/notifications/generate/missing-pay-alerts');
    return response.data;
  },

  // Get notification preferences
  getPreferences: async () => cachedGet('notifications/preferences', async () => {
    const response = await apiClient.get('/notifications/preferences');
    return response.data;
  }),

  // Update notification preferences
  updatePreferences: async (prefs) => {
    const response = await apiClient.put('/notifications/preferences', prefs);
    return response.data;
  },
};


// ==================== GPS Check-In/Check-Out API ====================

export const checkinAPI = {
  // Check in a staff member (manager action)
  checkIn: async (eventId, staffId, latitude, longitude, accuracy = null, notes = null, timezone = null) => {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const response = await apiClient.post('/checkin/in', {
      eventId,
      staffId,
      latitude,
      longitude,
      accuracy,
      notes,
      timezone: tz
    });
    return response.data;
  },

  // Staff self check-in (with timezone for accurate time comparison)
  selfCheckIn: async (eventId, latitude, longitude, accuracy = null, notes = null) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const response = await apiClient.post('/checkin/self-checkin', {
      eventId,
      latitude,
      longitude,
      accuracy,
      notes,
      timezone: tz
    });
    return response.data;
  },

  // Get today's events for staff self check-in (with timezone for accurate "today")
  getMyTodayEvents: async () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const response = await apiClient.get(`/checkin/my-today-events?tz=${encodeURIComponent(tz)}`);
    return response.data;
  },

  // Check out a staff member
  checkOut: async (eventId, staffId, latitude, longitude, accuracy = null, notes = null) => {
    const response = await apiClient.post('/checkin/out', {
      eventId,
      staffId,
      latitude,
      longitude,
      accuracy,
      notes
    });
    return response.data;
  },

  // Get check-in status for an event
  getEventStatus: async (eventId) => {
    const response = await apiClient.get(`/checkin/status/${eventId}`);
    return response.data;
  },

  // Get check-in history for a staff member
  getStaffHistory: async (staffId, limit = 20) => {
    const response = await apiClient.get(`/checkin/history/${staffId}?limit=${limit}`);
    return response.data;
  },

  // Get all active check-ins
  getActiveCheckins: async () => {
    const response = await apiClient.get('/checkin/active');
    return response.data;
  },

  // Update location coordinates for geofencing
  updateLocationCoords: async (locationId, latitude, longitude, accuracy = null) => {
    const response = await apiClient.put(`/checkin/location/${locationId}`, {
      latitude,
      longitude,
      accuracy
    });
    return response.data;
  },
};


// ==================== Announcements API ====================

export const announcementsAPI = {
  // Send an announcement email to staff. Optional Staff-Bulletin V1
  // metadata (eventId, resourceLink, etc.) is passed via the `extras`
  // bag so the existing 3-arg call sites stay 100% compatible.
  send: async (subject, message, staffIds, extras = {}) => {
    const payload = { subject, message, staff_ids: staffIds };
    const allowed = [
      'eventId', 'eventName',
      'resourceLink', 'resourceLabel',
      'attachmentUrl', 'recipientMode',
    ];
    for (const key of allowed) {
      if (extras && extras[key]) payload[key] = extras[key];
    }
    const response = await apiClient.post('/announcements/send', payload);
    return response.data;
  },

  // Get announcement history
  getHistory: async (limit = 20) => {
    const response = await apiClient.get(`/announcements/history?limit=${limit}`);
    return response.data;
  },

  // Get a specific announcement
  getById: async (announcementId) => {
    const response = await apiClient.get(`/announcements/${announcementId}`);
    return response.data;
  },

  // Staff Bulletin V1 — staff-scoped feed (only bulletins sent to
  // the current user's staff record)
  getStaffFeed: async (limit = 50) => {
    const response = await apiClient.get(`/announcements/staff/feed?limit=${limit}`);
    return response.data;
  },

  // Staff Bulletin V1 — bulletins linked to a specific event
  getByEvent: async (eventId, limit = 20) => {
    const response = await apiClient.get(`/announcements/by-event/${encodeURIComponent(eventId)}?limit=${limit}`);
    return response.data;
  },

  // Staff Bulletin V1 — resolve staff IDs assigned to an event
  getStaffByEvent: async (eventId) => {
    const response = await apiClient.get(`/announcements/staff-by-event/${encodeURIComponent(eventId)}`);
    return response.data;
  },
};


// ==================== Heat Automation API ====================

export const heatAPI = {
  // Get points configuration
  getConfig: async () => {
    const response = await apiClient.get('/heat-auto/points-config');
    return response.data;
  },

  // Award social tag points
  awardSocialTag: async (staffId, eventId = null, platform = 'instagram') => {
    const response = await apiClient.post('/heat-auto/social-tag', {
      staffId,
      eventId,
      platform
    });
    return response.data;
  },

  // Quick rate staff after event
  quickRate: async (staffId, eventId, rating, note = '') => {
    const response = await apiClient.post('/heat-auto/quick-rate', {
      staffId,
      eventId,
      rating,
      note
    });
    return response.data;
  },

  // Report no-show
  reportNoShow: async (staffId, eventId) => {
    const response = await apiClient.post(`/heat-auto/report-no-show/${staffId}/${eventId}`);
    return response.data;
  },

  // Get heat transactions
  getTransactions: async (staffId, limit = 50) => {
    const response = await apiClient.get(`/heat-auto/transactions/${staffId}?limit=${limit}`);
    return response.data;
  },

  // Get social tags history
  getSocialTags: async (staffId) => {
    const response = await apiClient.get(`/heat-auto/social-tags/${staffId}`);
    return response.data;
  },

  // Get leaderboard
  getLeaderboard: async (limit = 20) => {
    const response = await apiClient.get(`/heat-auto/leaderboard?limit=${limit}`);
    return response.data;
  },

  // Get top performer for dashboard
  getTopPerformer: async () => {
    const response = await apiClient.get('/leaderboard/top-performer');
    return response.data;
  },

  // Get staff heat summary
  getSummary: async (staffId) => {
    const response = await apiClient.get(`/heat-auto/summary/${staffId}`);
    return response.data;
  },

  // Get pending ratings (events completed but staff not rated)
  getPendingRatings: async () => {
    const response = await apiClient.get('/heat-auto/pending-ratings');
    return response.data;
  },

  // Dismiss a pending rating
  dismissRating: async (staffId, eventId) => {
    const response = await apiClient.post(`/heat-auto/dismiss-rating?staff_id=${staffId}&event_id=${eventId}`);
    return response.data;
  },
};


// ==================== Non-Circumvention API ====================

export const platformAPI = {
  // Get protected planner info (for vendors)
  getPlannerInfo: async (plannerId, bookingId = null) => {
    const params = bookingId ? `?booking_id=${bookingId}` : '';
    const response = await apiClient.get(`/platform/planner-info/${plannerId}${params}`);
    return response.data;
  },

  // Check messaging status for a booking
  getMessagingStatus: async (bookingId) => {
    const response = await apiClient.get(`/platform/messaging-status/${bookingId}`);
    return response.data;
  },

  // Check repeat booking eligibility
  checkRepeatBooking: async (vendorId) => {
    const response = await apiClient.get(`/platform/repeat-booking-check/${vendorId}`);
    return response.data;
  },

  // Send a message through platform
  sendMessage: async (recipientId, bookingId, message) => {
    const response = await apiClient.post('/platform/send-message', {
      recipientId,
      bookingId,
      message
    });
    return response.data;
  },

  // Get booking messages
  getMessages: async (bookingId) => {
    const response = await apiClient.get(`/platform/messages/${bookingId}`);
    return response.data;
  },

  // Get circumvention logs (admin)
  getCircumventionLogs: async () => {
    const response = await apiClient.get('/platform/circumvention-logs');
    return response.data;
  },
};


// ==================== Event Control Center API ====================

export const controlCenterAPI = {
  // Get full control center data for an event
  getData: async (eventId) => {
    const response = await apiClient.get(`/control-center/${eventId}`);
    return response.data;
  },

  // Batch live enrichment for multiple events (Control Center dashboard)
  getLiveEnrichment: async (eventIds) => {
    const response = await apiClient.post('/control-center/live-enrichment/batch', { eventIds });
    return response.data?.enrichment || {};
  },

  // Batch canonical readiness (skip/complete-aware module truth) for the
  // Command Deck, so priority/escalation scores on real module state instead
  // of pipeline-stage inference. Returns { [eventId]: readinessSummary }.
  getReadinessBatch: async (eventIds) => {
    const response = await apiClient.post('/events/readiness/batch', { eventIds });
    return response.data?.readiness || {};
  },

  // Day-of run-of-show progression. Writes liveStatus by stable
  // itemId (TLI-xxxx) — NOT by array index. This makes the check-off
  // resilient when the displayed order diverges from storage order.
  // Planning `status` is never affected.
  setTimelineLiveStatus: async (eventId, itemId, liveStatus) => {
    const response = await apiClient.patch(
      `/control-center/${eventId}/timeline-items/${itemId}`,
      { liveStatus }
    );
    return response.data;
  },

  // Report an issue
  reportIssue: async (eventId, description, severity = 'medium', assignedTo = null) => {
    const response = await apiClient.post(`/control-center/${eventId}/issues`, {
      description,
      severity,
      assignedTo
    });
    return response.data;
  },

  // Generate shareable control center link
  generateShareLink: async (eventId, label = null) => {
    const response = await apiClient.post(`/live/share/${eventId}`, { label });
    return response.data;
  },

  // Delegate an event: email someone a secure operate link (no account needed)
  delegateByEmail: async (eventId, email, label = null) => {
    const response = await apiClient.post(`/live/share/${eventId}/email`, { email, label });
    return response.data;
  },

  // Delegate ALL events: email someone a standing operator link
  delegateAllByEmail: async (email, label = null) => {
    const response = await apiClient.post(`/live/share-all`, { email, label });
    return response.data;
  },
  // List every share link minted for an event (owner-facing)
  listShareLinks: async (eventId) => {
    const response = await apiClient.get(`/live/share/${eventId}/links`);
    return response.data;
  },
  // Revoke a single share link by its token
  revokeShareLink: async (token) => {
    const response = await apiClient.delete(`/live/share/${token}`);
    return response.data;
  },

  // Save the external event planner's contact info onto the event
  savePlanner: async (eventId, planner) => {
    const response = await apiClient.put(`/control-center/${eventId}/planner`, planner);
    return response.data;
  },

  // Resolve an issue
  resolveIssue: async (eventId, issueId) => {
    const response = await apiClient.patch(`/control-center/${eventId}/issues/${issueId}/resolve`);
    return response.data;
  },

  // Get all issues for an event
  getIssues: async (eventId, includeResolved = false) => {
    const response = await apiClient.get(`/control-center/${eventId}/issues?include_resolved=${includeResolved}`);
    return response.data;
  },

  // Send a quick message
  sendMessage: async (eventId, recipientType, message, recipientId = null, sendEmail = true, sendPush = true) => {
    const response = await apiClient.post(`/control-center/${eventId}/send-message`, {
      recipientType,
      recipientId,
      message,
      sendEmail,
      sendPush
    });
    return response.data;
  },

  // Broadcast to all staff
  broadcast: async (eventId, message) => {
    const response = await apiClient.post(`/control-center/${eventId}/broadcast?message=${encodeURIComponent(message)}`);
    return response.data;
  },
};


// ==================== Staff Notifications API ====================

export const staffNotificationsAPI = {
  // Send batch notifications to newly assigned staff (skips already notified)
  batchNotify: async (eventId, staffIds) => {
    const response = await apiClient.post('/staff-notifications/batch-notify', {
      event_id: eventId,
      staff_ids: staffIds
    });
    return response.data;
  },

  // Send a reminder to a specific staff member
  sendReminder: async (staffId, eventId) => {
    const response = await apiClient.post('/staff-notifications/send-reminder', {
      staff_id: staffId,
      event_id: eventId
    });
    return response.data;
  },

  // Manually send an invite to a specific staff member for an event
  sendManualInvite: async (staffId, eventId) => {
    const response = await apiClient.post('/staff-notifications/send-manual-invite', {
      staff_id: staffId,
      event_id: eventId
    });
    return response.data;
  },

  // Get notification status for all staff on an event
  getNotificationStatus: async (eventId) => {
    const response = await apiClient.get(`/staff-notifications/notification-status/${eventId}`);
    return response.data;
  },

  // Get invite status for all staff on an event (shows who has been sent invites)
  getInviteStatus: async (eventId) => {
    const response = await apiClient.get(`/staff-notifications/invite-status/${eventId}`);
    return response.data;
  },

  // Preview booking email (for testing)
  previewEmail: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const response = await apiClient.get(`/staff-notifications/booking-preview?${queryParams}`);
    return response.data;
  },
};


// ==================== Activity Feed API ====================

export const activityFeedAPI = {
  // Get activity feed with pagination and filters
  getFeed: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.skip) params.append('skip', options.skip);
    if (options.activityType) params.append('activity_type', options.activityType);
    if (options.importance) params.append('importance', options.importance);
    if (options.sinceMinutes) params.append('since_minutes', options.sinceMinutes);
    const response = await apiClient.get(`/activity/feed?${params.toString()}`);
    return response.data;
  },

  // Get latest activities since a timestamp (for real-time polling)
  getLatest: async (since = null, limit = 10) => {
    const params = new URLSearchParams();
    if (since) params.append('since', since);
    params.append('limit', limit);
    const response = await apiClient.get(`/activity/feed/latest?${params.toString()}`);
    return response.data;
  },

  // Get activity statistics
  getStats: async () => {
    const response = await apiClient.get('/activity/stats');
    return response.data;
  },

  // Log a new activity (manual/testing)
  log: async (activityData) => {
    const response = await apiClient.post('/activity/log', activityData);
    return response.data;
  },

  // Seed sample data (development only)
  seedSampleData: async () => {
    const response = await apiClient.post('/activity/seed-sample-data');
    return response.data;
  }
};


// ==================== Email Tracking API ====================

export const emailTrackingAPI = {
  getSummary: async (days = 30) => {
    const response = await apiClient.get(`/email-tracking/analytics/summary?days=${days}`);
    return response.data;
  },
  getInvites: async (days = 30, limit = 10) => {
    const response = await apiClient.get(`/email-tracking/analytics/invites?days=${days}&limit=${limit}`);
    return response.data;
  },
  getFollowupCandidates: async (hoursSinceOpen = 24, limit = 20) => {
    const response = await apiClient.get(`/email-tracking/followup-candidates?hours_since_open=${hoursSinceOpen}&limit=${limit}`);
    return response.data;
  },
  getFollowupStats: async (days = 30) => {
    const response = await apiClient.get(`/email-tracking/analytics/followups?days=${days}`);
    return response.data;
  },
  sendFollowups: async (hoursSinceOpen = 24, maxEmails = 50) => {
    const response = await apiClient.post(`/email-tracking/send-followups?hours_since_open=${hoursSinceOpen}&max_emails=${maxEmails}`);
    return response.data;
  },
};

// ==================== Push Notifications API ====================

export const pushNotificationsAPI = {
  getStatus: async () => {
    const response = await apiClient.get('/push-notifications/status');
    return response.data;
  },
  getVapidPublicKey: async () => {
    const response = await apiClient.get('/push-notifications/vapid-public-key');
    return response.data;
  },
  subscribe: async (subscription) => {
    const response = await apiClient.post('/push-notifications/subscribe', { subscription });
    return response.data;
  },
  unsubscribe: async (subscription) => {
    const response = await apiClient.post('/push-notifications/unsubscribe', { subscription });
    return response.data;
  },
  sendTest: async () => {
    const response = await apiClient.post('/push-notifications/test');
    return response.data;
  },
};

// ==================== Event Chat API ====================

export const eventChatAPI = {
  // Get chatroom for an event with messages
  getChat: async (eventId, since = null, limit = 50) => {
    const params = new URLSearchParams();
    if (since) params.append('since', since);
    params.append('limit', limit);
    const response = await apiClient.get(`/chat/event/${eventId}?${params.toString()}`);
    return response.data;
  },

  // Send a message to the event chatroom
  sendMessage: async (eventId, content, attachments = []) => {
    const response = await apiClient.post(`/chat/event/${eventId}`, {
      content,
      attachments
    });
    return response.data;
  },

  // Get chat participants
  getParticipants: async (eventId) => {
    const response = await apiClient.get(`/chat/event/${eventId}/participants`);
    return response.data;
  },

  // Mark messages as read
  markRead: async (eventId) => {
    const response = await apiClient.post(`/chat/event/${eventId}/mark-read`);
    return response.data;
  },

  // Get all chatrooms the user has access to
  getMyChats: async () => {
    const response = await apiClient.get('/chat/my-chats');
    return response.data;
  }
};
